import { QUERY_MAP } from "~/constants";
import { useCallback, useState } from "react";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";

function tableSortQuery({ items, sortId, setSortId, resetSortId, sortDirection, setSortDirection, resetSortDirection }) {
    const sortedByItems = items?.filter((item) => item?.defaultSortedBy);
    if (sortedByItems?.length > 1) {
        throw Error("Only one defaultSortedBy item allowed");
    }

    let sortItem = items?.find((item) => item?.id === sortId);

    const resetSort = useCallback(() => {
        resetSortId();
        resetSortDirection();
    }, [resetSortId, resetSortDirection]);

    const sortIdServiceMapperFn = sortItem?.sortIdServiceMapperFn || (({ sortId }) => sortId);
    const serviceSortId = sortIdServiceMapperFn({ sortId, sortDirection });

    const sort = sortId ? `${serviceSortId},${sortDirection || ""}` : undefined;
    return {
        sortId,
        sortDirection,
        setSortId,
        setSortDirection,
        sort,
        resetSort,
        resetSortId,
        resetSortDirection,
    };
}

function getDefaultSortedByItem({ items }) {
    const sortedByItems = items?.filter((item) => item?.defaultSortedBy);
    if (sortedByItems?.length > 1) {
        throw Error("Only one defaultSortedBy item allowed");
    }

    return sortedByItems?.[0];
}

export function useTableSortQuery({ items = [] }) {
    const defaultSortedByItem = getDefaultSortedByItem({ items });
    const defaultSortedByItemId = defaultSortedByItem?.id;

    const [sortId, setSortId, resetSortId] = useQueryState({
        key: QUERY_MAP.TABLE_SORT,
        defaultValue: defaultSortedByItemId,
    });
    const [sortDirection, setSortDirection, resetSortDirection] = useQueryState({
        key: QUERY_MAP.TABLE_SORT_DIR,
        defaultValue: defaultSortedByItem?.defaultSortDirection || "asc",
    });

    return tableSortQuery({
        items,
        sortId,
        setSortId,
        resetSortId,
        sortDirection,
        setSortDirection,
        resetSortDirection,
    });
}

export function useTableSortNoQuery({ items = [] }) {
    const defaultSortedByItem = getDefaultSortedByItem({ items });
    const defaultSortedByItemId = defaultSortedByItem?.id;

    const [sortId, setSortId] = useState(defaultSortedByItemId);

    const [sortDirection, setSortDirection] = useState(defaultSortedByItem?.defaultSortDirection || "asc");

    const resetSortId = useCallback(() => {
        setSortId(defaultSortedByItemId);
    }, [defaultSortedByItemId]);

    const resetSortDirection = useCallback(() => {
        setSortDirection(defaultSortedByItem?.defaultSortDirection || "asc");
    }, [defaultSortedByItem]);

    return tableSortQuery({
        items,
        sortId,
        setSortId,
        resetSortId,
        sortDirection,
        setSortDirection,
        resetSortDirection,
    });
}
